import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// One field per value, quoted only when it contains something that would
// otherwise break the CSV grid (comma, quote, or a line break) — Excel reads
// unquoted plain values just fine and quoting everything makes the file
// noisier to eyeball.
function csvCell(value) {
  let s = String(value ?? '');
  // Directory fields (name, title, …) can carry admin corrections or synced
  // values we don't fully control. A cell starting with =, +, -, @, tab, or
  // CR is executed as a formula by Excel/Sheets on open — neutralize it with
  // a leading apostrophe before the usual comma/quote/newline escaping.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCSV(employees) {
  const header = ['Name', 'Title', 'Department', 'Office', 'Email'];
  const rows = employees.map((emp) => [
    emp.displayName || '',
    emp.jobTitle || '',
    emp.department || '',
    emp.officeLocation || '',
    emp.mail || '',
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

function blobUrlToDataURL(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 80;
        canvas.height = img.naturalHeight || 80;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function pdfHeader(selectedCount, date) {
  return `
  <div class="header">
    <div class="brand-row">
      <span class="brand-people">People</span><span class="brand-vue">VUE</span>
    </div>
    <div class="brand-sub">by MOREgroup</div>
    <div class="header-meta">
      <span class="export-title">Employee Directory Export &nbsp;&middot;&nbsp; ${date}</span>
      <span class="count-pill">${selectedCount} ${selectedCount === 1 ? 'employee' : 'employees'}</span>
    </div>
  </div>`;
}

function pdfFooter(date) {
  return `
  <div class="footer">
    <span>MOREgroup Solutions Development &nbsp;&middot;&nbsp; PeopleVUE &copy; 2026</span>
    <span>Generated ${date}</span>
  </div>`;
}

const BASE_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;900&display=swap');
  body {
    font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: #f5f5f7;
    color: #1a1a1a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .header {
    background: linear-gradient(135deg, #1c1c1e 0%, #2a2a2d 60%, #0f2e2b 100%);
    padding: 36px 48px 32px;
    position: relative;
    overflow: hidden;
  }
  .header::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 220px; height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(94,234,212,0.18) 0%, transparent 70%);
  }
  .header::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #5eead4, #0d9488);
  }
  .brand-row { display: flex; align-items: baseline; margin-bottom: 4px; }
  .brand-people { font-size: 32px; font-weight: 900; color: #fff; letter-spacing: -1px; }
  .brand-vue { font-size: 32px; font-weight: 900; color: #5eead4; letter-spacing: -1px; }
  .brand-sub { font-size: 11px; color: rgba(255,255,255,0.35); letter-spacing: 2px; text-transform: uppercase; margin-top: 2px; }
  .header-meta { display: flex; align-items: center; justify-content: space-between; margin-top: 20px; }
  .export-title { font-size: 14px; color: rgba(255,255,255,0.55); }
  .count-pill {
    background: rgba(94,234,212,0.15);
    border: 1px solid rgba(94,234,212,0.3);
    color: #5eead4;
    padding: 4px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }
  .footer {
    padding: 16px 48px;
    border-top: 1px solid #e2e8e7;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
  }
  .footer span { font-size: 11px; color: #9ca3af; }
  @media print {
    @page { margin: 0; size: A4 portrait; }
    body { margin: 0; background: #fff; }
  }
`;

function buildListHTML(employees, photoData, date) {
  const rows = employees.map((emp, i) => {
    const initials = (emp.displayName || '??').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
    const bg = i % 2 === 0 ? '#ffffff' : '#f8fffe';
    const photoSrc = photoData[emp.id];
    const avatar = photoSrc
      ? `<img src="${photoSrc}" style="width:38px;height:38px;border-radius:50%;object-fit:cover;flex-shrink:0;border:2px solid #e2f8f5;" />`
      : `<div style="width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,#5eead4,#0d9488);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
           <span style="color:#fff;font-size:12px;font-weight:700;">${initials}</span>
         </div>`;
    return `
      <tr style="background:${bg}">
        <td style="padding:10px 16px;border-bottom:1px solid #e2f8f5;">
          <div style="display:flex;align-items:center;gap:12px;">
            ${avatar}
            <div>
              <div style="font-weight:600;color:#1a1a1a;font-size:13px;">${emp.displayName || '&mdash;'}</div>
              <div style="color:#6b7280;font-size:11px;margin-top:2px;">${emp.jobTitle || '&mdash;'}</div>
            </div>
          </div>
        </td>
        <td style="padding:10px 16px;border-bottom:1px solid #e2f8f5;color:#374151;font-size:12px;vertical-align:middle;">${emp.department || '&mdash;'}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #e2f8f5;color:#374151;font-size:12px;vertical-align:middle;">${emp.officeLocation || '&mdash;'}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #e2f8f5;color:#0d9488;font-size:12px;vertical-align:middle;">${emp.mail || '&mdash;'}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/>
  <title>PeopleVUE Export &mdash; ${date}</title>
  <style>
    ${BASE_STYLES}
    .content { padding: 0 48px 40px; background: #f5f5f7; }
    .table-wrap { margin-top: 28px; border-radius: 12px; overflow: hidden; border: 1px solid #e2f8f5; box-shadow: 0 1px 4px rgba(13,148,136,0.06); }
    table { width: 100%; border-collapse: collapse; background: #fff; }
    thead tr { background: linear-gradient(90deg, #0d9488, #0f766e); }
    th { padding: 11px 16px; text-align: left; font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.8); letter-spacing: 0.8px; text-transform: uppercase; }
  </style></head><body>
  ${pdfHeader(employees.length, date)}
  <div class="content">
    <div class="table-wrap">
      <table>
        <thead><tr><th>Employee</th><th>Department</th><th>Office</th><th>Email</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  </div>
  ${pdfFooter(date)}
  <script>window.onload = () => window.print();</script>
  </body></html>`;
}

function buildCardHTML(employees, photoData, date) {
  const cards = employees.map((emp) => {
    const initials = (emp.displayName || '??').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
    const photoSrc = photoData[emp.id];

    const photoBanner = photoSrc
      ? `<img src="${photoSrc}" style="width:100%;height:100%;object-fit:cover;object-position:top;display:block;" />`
      : `<div style="width:100%;height:100%;background:linear-gradient(135deg,#5eead4,#0d9488);display:flex;align-items:center;justify-content:center;">
           <span style="color:#fff;font-size:40px;font-weight:800;letter-spacing:-1px;">${initials}</span>
         </div>`;

    const dept = emp.department
      ? `<span style="display:inline-block;padding:3px 8px;background:#f1f5f9;color:#475569;border-radius:999px;font-size:10px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">${emp.department}</span>`
      : '';

    const location = emp.officeLocation
      ? `<div style="display:flex;align-items:center;gap:4px;margin-top:auto;padding-top:7px;border-top:1px solid #f1f5f9;">
           <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
           <span style="font-size:10px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${emp.officeLocation}</span>
         </div>`
      : '';

    return `
      <div style="background:#fff;border-radius:14px;overflow:hidden;border:1px solid #e2e8f0;box-shadow:0 1px 4px rgba(0,0,0,0.06);display:flex;flex-direction:column;">
        <div style="height:140px;overflow:hidden;background:#f1f5f9;flex-shrink:0;">
          ${photoBanner}
        </div>
        <div style="height:3px;background:linear-gradient(90deg,#5eead4,#0d9488);flex-shrink:0;"></div>
        <div style="padding:11px 13px 13px;flex:1;display:flex;flex-direction:column;gap:3px;">
          <div style="font-weight:700;font-size:12.5px;color:#0f172a;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${emp.displayName || '&mdash;'}</div>
          <div style="font-size:10.5px;color:#64748b;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${emp.jobTitle || '&mdash;'}</div>
          <div style="margin-top:5px;">${dept}</div>
          ${location}
        </div>
      </div>`;
  }).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/>
  <title>PeopleVUE Export &mdash; ${date}</title>
  <style>
    ${BASE_STYLES}
    .content { padding: 28px 40px 40px; }
    .card-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; align-items: stretch; }
    @media print {
      @page { margin: 0; size: A4 portrait; }
      .card-grid { grid-template-columns: repeat(4, 1fr); }
    }
  </style></head><body>
  ${pdfHeader(employees.length, date)}
  <div class="content">
    <div class="card-grid">${cards}</div>
  </div>
  ${pdfFooter(date)}
  <script>window.onload = () => window.print();</script>
  </body></html>`;
}

export function SelectionActionBar({ selectedEmployees, onClearSelection, photos = {}, displayMode = 'list' }) {
  const [exporting, setExporting] = useState(false);
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const selectedCount = selectedEmployees.length;

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handle = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [exportMenuOpen]);

  if (selectedCount === 0) return null;

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const photoEntries = await Promise.all(
        selectedEmployees.map(async (emp) => {
          const url = photos[emp.id];
          const data = url ? await blobUrlToDataURL(url) : null;
          return [emp.id, data];
        })
      );
      const photoData = Object.fromEntries(photoEntries);
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const html = displayMode === 'card'
        ? buildCardHTML(selectedEmployees, photoData, date)
        : buildListHTML(selectedEmployees, photoData, date);

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'width=960,height=720');
      if (win) win.addEventListener('afterprint', () => URL.revokeObjectURL(url));
    } finally {
      setExporting(false);
      setExportMenuOpen(false);
    }
  };

  const handleExportExcel = () => {
    setExporting(true);
    try {
      const csv = buildCSV(selectedEmployees);
      // A UTF-8 BOM keeps Excel from mangling non-ASCII names on open.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PeopleVUE Export ${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
      setExportMenuOpen(false);
    }
  };

  const handleCopyEmailAddresses = async () => {
    const emails = [...new Set(
      selectedEmployees
        .map((employee) => employee.mail?.trim())
        .filter(Boolean),
    )];
    if (emails.length === 0) return;

    try {
      await navigator.clipboard.writeText(emails.join('; '));
      setCopiedEmails(true);
      window.setTimeout(() => setCopiedEmails(false), 1600);
    } catch {
      // Clipboard may be blocked by browser policy; fail quietly rather than
      // interrupting the selection flow with an alert.
    }
  };

  return (
    <AnimatePresence>
      {selectedCount > 0 && (
        <motion.div
          key="selection-bar"
          initial={{ opacity: 0, y: 120 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 120 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 sm:bottom-6 left-0 right-0 z-50 flex justify-center pointer-events-none"
        >
          <div className="bg-[#1c1c1e] text-white rounded-2xl shadow-2xl shadow-black/40 px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-3 sm:gap-5 pointer-events-auto border border-white/10">
            {/* Count */}
            <div className="flex items-center gap-2 pr-3 sm:pr-5 border-r border-white/10">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-sm font-bold text-white">
                {selectedCount}
              </div>
              <span className="text-sm text-white/60 hidden sm:inline">
                {selectedCount === 1 ? 'person' : 'people'} selected
              </span>
            </div>

            {/* Mode indicator */}
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-white/30">
              {displayMode === 'card' ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
              {displayMode === 'card' ? 'Card' : 'List'} layout
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyEmailAddresses}
                disabled={exporting}
                className="flex items-center gap-2 px-3 py-2 rounded-xl font-medium text-sm
                  bg-white/[0.06] hover:bg-white/[0.1] border border-white/10
                  text-white transition-all active:scale-95 disabled:opacity-60"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {copiedEmails ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M5 13l4 4L19 7" />
                  ) : (
                    <>
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M5 15V5a2 2 0 012-2h10" />
                    </>
                  )}
                </svg>
                <span>{copiedEmails ? 'Copied Email Addresses' : 'Copy Email Addresses'}</span>
              </button>

              <div className="relative" ref={exportMenuRef}>
                <AnimatePresence>
                  {exportMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full right-0 mb-2 w-44 rounded-xl bg-[#2a2a2d] border border-white/10 overflow-hidden shadow-2xl shadow-black/40 z-10"
                    >
                      <button
                        onClick={handleExportPDF}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-white/85 hover:bg-white/[0.08] transition-colors border-b border-white/10"
                      >
                        <DocumentIcon className="w-4 h-4 flex-shrink-0 text-teal-400" />
                        Export PDF
                      </button>
                      <button
                        onClick={handleExportExcel}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-white/85 hover:bg-white/[0.08] transition-colors"
                      >
                        <SpreadsheetIcon className="w-4 h-4 flex-shrink-0 text-emerald-500" />
                        Export Excel
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  onClick={() => setExportMenuOpen((o) => !o)}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm
                    bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500
                    text-white transition-all active:scale-95 disabled:opacity-60 disabled:cursor-wait"
                >
                  {exporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Preparing…</span>
                    </>
                  ) : (
                    <>
                      <DocumentIcon className="w-4 h-4" />
                      <span>Export</span>
                      <svg className={`w-3.5 h-3.5 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Clear */}
            <button
              onClick={onClearSelection}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.08] transition-colors"
              title="Clear selection"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DocumentIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-2-2m2 2l2-2" />
    </svg>
  );
}

function SpreadsheetIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  );
}
